import React from 'react';
import { Editor } from 'grapesjs';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Save,
  Rocket,
  Eye,
  Monitor,
  Tablet,
  Smartphone,
  Undo,
  Redo,
  Code,
  Sparkles,
  Trash2,
  Loader2,
  BookMarked,
  FileCode,
  Download,
  Settings,
  Wand2,
  RotateCcw,
} from 'lucide-react';
import Button from '../../ui/Button';
import LanguageSelector from '../../ui/LanguageSelector';

interface PageBuilderToolbarProps {
  pageName: string;
  pageSlug: string;
  onPageNameChange: (name: string) => void;
  onBack: () => void;
  onSave: () => void;
  onPublish: () => void;
  onPreview: () => void;
  onSaveAsTemplate: () => void;
  onCustomCode: () => void;
  onSettings: () => void;
  // Só definido para páginas de HTML importado — abre o editor de HTML completo.
  onEditFullHtml?: () => void;
  // Só definido para páginas HTML — abre o chat "Editar com IA".
  onAIEdit?: () => void;
  editor: Editor | null;
  isSaving?: boolean;
  isPublishing?: boolean;
  // Página ainda sendo carregada do servidor: Salvar/Publicar ficam travados
  // (salvar agora gravaria o editor vazio por cima do conteúdo do cliente).
  isPageLoading?: boolean;
  activeDevice: 'Desktop' | 'Tablet' | 'Mobile';
  onDeviceChange: (device: 'Desktop' | 'Tablet' | 'Mobile') => void;
  // Ajuste automático de responsividade (só aparece em Tablet/Celular).
  onAutoResponsive?: () => void;
  onUndoResponsive?: () => void;
  hasResponsiveFix?: boolean;
  isFixingResponsive?: boolean;
}

const PageBuilderToolbar: React.FC<PageBuilderToolbarProps> = ({
  pageName,
  pageSlug,
  onPageNameChange,
  onBack,
  onSave,
  onPublish,
  onPreview,
  onSaveAsTemplate,
  onCustomCode,
  onSettings,
  onEditFullHtml,
  onAIEdit,
  editor,
  isSaving = false,
  isPublishing = false,
  isPageLoading = false,
  activeDevice,
  onDeviceChange,
  onAutoResponsive,
  onUndoResponsive,
  hasResponsiveFix = false,
  isFixingResponsive = false
}) => {
  const { t } = useTranslation();
  const handleUndo = () => {
    if (editor) {
      editor.UndoManager.undo();
    }
  };

  const handleRedo = () => {
    if (editor) {
      editor.UndoManager.redo();
    }
  };

  const handleClear = () => {
    if (editor && window.confirm(t('pageBuilder.toolbar.clearConfirm', 'Tem certeza que deseja limpar todo o conteudo?'))) {
      editor.DomComponents.clear();
      editor.CssComposer.clear();
    }
  };

  const handleExportCode = () => {
    if (!editor) return;

    const html = editor.getHtml();
    let css = editor.getCss() || '';

    // Get wrapper (body) styles - GrapesJS doesn't include these by default
    const wrapper = editor.getWrapper();
    if (wrapper) {
      const wrapperStyle = wrapper.getStyle();
      if (wrapperStyle && Object.keys(wrapperStyle).length > 0) {
        const wrapperCss = Object.entries(wrapperStyle)
          .map(([prop, val]) => `${prop}: ${val};`)
          .join(' ');
        css = `body { ${wrapperCss} }\n${css}`;
      }
    }

    const fullHtml = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pageName}</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    body { font-family: 'Inter', sans-serif; }
    ${css}
  </style>
</head>
<body>
  ${html}
</body>
</html>`;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pageSlug || 'landing-page'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <header className="h-14 bg-gray-900 border-b border-gray-700 flex items-center justify-between px-2 md:px-4 flex-shrink-0 gap-1">
      {/* Left section */}
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-gray-400 hover:text-white hover:bg-gray-800 flex-shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>

        <div className="h-6 w-px bg-gray-700 hidden md:block" />

        <div className="flex flex-col min-w-0">
          <input
            type="text"
            value={pageName}
            onChange={(e) => onPageNameChange(e.target.value)}
            className="font-medium text-white bg-transparent border-none focus:outline-none focus:ring-0 p-0 text-sm truncate w-full"
            placeholder={t('pageBuilder.settings.general.name', 'Nome da pagina')}
          />
          <span className="text-xs text-gray-500 truncate hidden sm:block">/{pageSlug}</span>
        </div>
      </div>

      {/* Center section - Device selector */}
      <div className="hidden md:flex items-center bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
        <button
          onClick={() => onDeviceChange('Desktop')}
          className={`p-2.5 transition-colors ${
            activeDevice === 'Desktop'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
          title="Desktop"
        >
          <Monitor className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDeviceChange('Tablet')}
          className={`p-2.5 transition-colors ${
            activeDevice === 'Tablet'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
          title="Tablet"
        >
          <Tablet className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDeviceChange('Mobile')}
          className={`p-2.5 transition-colors ${
            activeDevice === 'Mobile'
              ? 'bg-indigo-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
          title="Mobile"
        >
          <Smartphone className="w-4 h-4" />
        </button>
      </div>

      {/* Ajuste automático de responsividade — só faz sentido fora do Desktop.
          Conserta seções "encavaladas" gravando CSS só na media query da tela. */}
      {activeDevice !== 'Desktop' && onAutoResponsive && (
        <div className="hidden md:flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onAutoResponsive}
            disabled={isFixingResponsive}
            data-testid="auto-responsive-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60 transition-colors"
            title={t(
              'pageBuilder.responsive.autoFixHint',
              'Organiza automaticamente as seções que estão encavaladas nesta tela (não altera o desktop)'
            )}
          >
            {isFixingResponsive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {t('pageBuilder.responsive.autoFix', 'Organizar esta tela')}
          </button>
          {hasResponsiveFix && onUndoResponsive && (
            <button
              onClick={onUndoResponsive}
              data-testid="undo-responsive-btn"
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
              title={t('pageBuilder.responsive.undoFix', 'Desfazer organização automática')}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Right section */}
      <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
        {/* Undo/Redo */}
        <div className="flex items-center bg-gray-800 rounded-lg overflow-hidden">
          <button
            onClick={handleUndo}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title={`${t('pageBuilder.toolbar.undo', 'Desfazer')} (Ctrl+Z)`}
          >
            <Undo className="w-4 h-4" />
          </button>
          <button
            onClick={handleRedo}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title={`${t('pageBuilder.toolbar.redo', 'Refazer')} (Ctrl+Shift+Z)`}
          >
            <Redo className="w-4 h-4" />
          </button>
        </div>

        <div className="h-6 w-px bg-gray-700 hidden md:block" />

        {/* Additional actions - hidden on small screens */}
        <button
          onClick={onSettings}
          className="hidden md:block p-2 text-gray-400 hover:text-indigo-400 hover:bg-gray-800 rounded-lg transition-colors"
          title={t('pageBuilder.settings.title', 'Configuracoes da Pagina')}
        >
          <Settings className="w-4 h-4" />
        </button>
        <button
          onClick={onCustomCode}
          className="hidden lg:block p-2 text-gray-400 hover:text-green-400 hover:bg-gray-800 rounded-lg transition-colors"
          title={t('pageBuilder.customCode.title', 'Codigo Customizado')}
        >
          <FileCode className="w-4 h-4" />
        </button>
        <button
          onClick={onSaveAsTemplate}
          className="hidden lg:block p-2 text-gray-400 hover:text-indigo-400 hover:bg-gray-800 rounded-lg transition-colors"
          title={t('pageBuilder.saveTemplate.title', 'Salvar como Template')}
        >
          <BookMarked className="w-4 h-4" />
        </button>
        <button
          onClick={handleExportCode}
          className="hidden lg:block p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          title={t('pageBuilder.toolbar.exportHtml', 'Exportar HTML')}
        >
          <Download className="w-4 h-4" />
        </button>
        <button
          onClick={handleClear}
          className="hidden lg:block p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
          title={t('pageBuilder.toolbar.clear', 'Limpar Canvas')}
        >
          <Trash2 className="w-4 h-4" />
        </button>

        <div className="h-6 w-px bg-gray-700 hidden lg:block" />

        {/* Language Selector */}
        <div className="hidden md:block">
          <LanguageSelector />
        </div>

        <div className="h-6 w-px bg-gray-700" />

        {/* Editar com IA — só para páginas HTML (geradas por IA / importadas) */}
        {onAIEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAIEdit}
            className="!bg-gradient-to-r !from-indigo-600 !to-purple-600 !text-white hover:!from-indigo-500 hover:!to-purple-500 border border-indigo-400"
            title={t('pageBuilder.aiEdit.title', 'Editar com IA')}
          >
            <Sparkles className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{t('pageBuilder.aiEdit.button', 'Editar com IA')}</span>
          </Button>
        )}

        {/* HTML completo — só para páginas importadas via HTML */}
        {onEditFullHtml && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onEditFullHtml}
            className="!bg-emerald-600/90 !text-white hover:!bg-emerald-600 border border-emerald-500"
            title={t('pageBuilder.fullHtml.title', 'Editar HTML completo')}
          >
            <Code className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">{t('pageBuilder.fullHtml.button', 'Editar HTML')}</span>
          </Button>
        )}

        {/* Main actions */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onPreview}
          className="text-gray-300 hover:text-white hover:bg-gray-800"
        >
          <Eye className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">{t('pageBuilder.toolbar.preview', 'Preview')}</span>
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={onSave}
          disabled={isSaving || isPageLoading}
          title={isPageLoading ? t('pageBuilder.toolbar.waitLoading', 'Aguarde a página terminar de carregar') : undefined}
          className="!bg-gray-700 !text-white hover:!bg-gray-600 border border-gray-600"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" />
          ) : (
            <Save className="w-4 h-4 sm:mr-1" />
          )}
          <span className="hidden sm:inline">{isSaving ? t('pageBuilder.toolbar.saving', 'Salvando...') : t('pageBuilder.toolbar.save', 'Salvar')}</span>
        </Button>

        <Button
          size="sm"
          onClick={onPublish}
          disabled={isPublishing || isPageLoading}
          title={isPageLoading ? t('pageBuilder.toolbar.waitLoading', 'Aguarde a página terminar de carregar') : undefined}
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          {isPublishing ? (
            <Loader2 className="w-4 h-4 sm:mr-1 animate-spin" />
          ) : (
            <Rocket className="w-4 h-4 sm:mr-1" />
          )}
          <span className="hidden sm:inline">{isPublishing ? t('pageBuilder.toolbar.publishing', 'Publicando...') : t('pageBuilder.toolbar.publish', 'Publicar')}</span>
        </Button>
      </div>
    </header>
  );
};

export default PageBuilderToolbar;
